import type { CastAnchor } from "../config/campaign";

export type CutoutResult = { image: HTMLImageElement; anchor: CastAnchor };

/** Mensagens que o apoiador entende, para cada recusa conhecida do serviço. */
const MESSAGES: Record<string, string> = {
  no_person: "Não encontramos ninguém nessa foto. Escolha uma em que você apareça inteiro.",
  no_face: "Não conseguimos ver seu rosto. Use uma foto de frente, com boa luz.",
  multiple_faces: "Essa foto tem mais de uma pessoa. Escolha uma foto só sua.",
  too_large: "A foto é grande demais. Escolha uma imagem menor.",
  rate_limited: "Muita gente criando santinho agora. Espere alguns segundos e tente de novo.",
};

export class CutoutError extends Error {
  constructor(public code: string) {
    super(MESSAGES[code] || "Não foi possível preparar sua foto. Tente outra imagem.");
  }
}

function decode(dataUrl: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new CutoutError("decode_failed"));
    image.src = dataUrl;
  });
}

/**
 * Recorta o apoiador do fundo e mede as âncoras faciais que alinham a figura no palco.
 * A medida vem junto do recorte porque quem segmenta já tem a máscara e o rosto na mão.
 */
export async function requestCutout(file: File, signal?: AbortSignal): Promise<CutoutResult> {
  const body = new FormData();
  body.append("photo", file);

  let response: Response;
  try {
    response = await fetch("/api/cutout", { method: "POST", body, signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new CutoutError("offline");
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new CutoutError(payload.error || (response.status === 429 ? "rate_limited" : "failed"));
  }

  const payload = (await response.json()) as { image: string; anchor: CastAnchor };
  return { image: await decode(payload.image), anchor: payload.anchor };
}
